/* eslint-disable @typescript-eslint/no-namespace */

export interface ColorDefinition {
  name?: string;
  hex: string;
  rgb: number[];
}

export namespace DataVisualization {
  export namespace Colors {
    export const TotalCo2Palette: ColorDefinition[] = [
      // Hot
      { hex: "#C00000", rgb: [192, 0, 0] },
      { hex: "#FF0000", rgb: [255, 0, 0] },
      { hex: "#FFC000", rgb: [255, 192, 0] },
      { hex: "#FFFF00", rgb: [255, 255, 0] },
      { hex: "#92D050", rgb: [140, 208, 80] },
      { hex: "#FFFFFF", rgb: [255, 255, 255] },
      // Cold
    ];

    export const MaterialsPalette: ColorDefinition[] = [
      // Hot
      { hex: "#C85734", rgb: [200, 87, 52] },
      { hex: "#5E60F5", rgb: [94, 96, 245] },
      { hex: "#5FBF7B", rgb: [95, 191, 123] },
      { hex: "#666766", rgb: [102, 103, 102] },
      { hex: "#68339B", rgb: [104, 51, 155] },
      { hex: "#CCCCCC", rgb: [204, 204, 204] },
      { hex: "#2F6FBA", rgb: [47, 111, 186] },
      { hex: "#091E5D", rgb: [9, 30, 93] },
      { hex: "#333333", rgb: [20, 20, 20] },
      { hex: "#D13252", rgb: [209, 50, 82] },
      // Cold
    ];

    // Q5 of Autodesk Color Spec
    export const Q5Palette = ["#CE6565", "#E0AF4B", "#E1E154", "#90D847", "#3BD23B", "#3BC580", "#3BBABA", "#689ED4", "#5178C8", "#9C6BCE", "#D46BC3", "#CE5C95"];

    export const getNextColor = (colors: any[], index: number): any => {
      return colors[index % colors.length];
    };

    export const calcPalette = (palette: ColorDefinition[], numColors: number): ColorDefinition[] => {
      const temp: ColorDefinition[] = palette.slice(0, palette.length);

      for (let i = 0; i < numColors; i++) {
        temp.push(calcNextColor(temp));
      }

      return temp;
    };

    export const calcNextColor = (colorSet: ColorDefinition[]): ColorDefinition => {
      if (colorSet.length < 10) {
        return MaterialsPalette[colorSet.length];
      }
      const hues: number[] = [];
      for (const c of colorSet) {
        hues.push(Math.round(RGBToHSV(ColorDefinitionToRGB(c)).h * 360));
      }
      hues.sort(function (a, b) {
        return a - b;
      });
      let lowH: number = 0.0;
      let interval: number = 0.0;
      if (hues.length === 1) {
        interval = 360.0;
        lowH = lowH * hues[hues.length - 1];
      } else {
        const count: number = hues.length;
        for (let ii = 0; ii < count; ++ii) {
          const currH: number = hues[ii];
          const nextH: number = ii === count - 1 ? hues[0] : hues[ii + 1];
          let newInterval: number = nextH - currH;
          if (newInterval < 0) {
            newInterval = 360.0 - currH + nextH;
          }
          if (newInterval > interval) {
            lowH = currH;
            interval = newInterval;
          }
        }
      }
      let hue: number = lowH + interval * 0.5;
      if (hue > 360) {
        hue -= 360;
      }
      const rgb = HSVtoRGB({ h: hue, s: 0.74, v: 0.78 });
      return { hex: RGBToHEX(rgb), rgb: [rgb.r, rgb.g, rgb.b] } as ColorDefinition;
    };
  }

  export const ColorDefinitionToRGB = (color: ColorDefinition): RGB => {
    return {
      r: color.rgb[0],
      g: color.rgb[1],
      b: color.rgb[2],
      a: 1,
    };
  };

  /**
   * 
   * @param RGB color which connverts to HSV
   * @returns HSV format color
   */
  export const RGBToHSV = (rgb: RGB): HSV => {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const v = max;

    const d = max - min;
    s = max === 0 ? 0 : d / max;

    if (max === min) {
      h = 0; // achromatic
    } else {
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }

      h /= 6;
    }

    return { h, s, v };
  };

  /**
  * 
  * @param hsv Gets HSV format color
  * @returns RGB object
  */
  export const HSVtoRGB = (hsv: HSV): RGB => {
    let HH: number = hsv.h;
    let SS: number = hsv.s;
    let VV: number = hsv.v;

    if (SS <= 0.0) {
      return { r: 0, g: 0, b: 0 };
    }
    else if (SS > 1.0) {
      SS = 1.0;
    }


    if (HH < 0) {
      HH = 0.0;
    }
    else if (HH > 360.0) {
      HH = 360.0;
    }
    if (VV < 0) {
      VV = 0.0;
    }
    else if (VV > 1.0) {
      VV = 1.0;
    }

    HH /= 60.0;
    const H1: number = Math.floor(HH);

    const V1: number = VV * 255;
    const dH = HH - H1;
    const pp = V1 * (1.0 - SS);
    const qq = V1 * (1.0 - SS * dH);
    const tt = V1 * (1.0 - SS * (1.0 - dH));

    let RR = 0;
    let GG = 0;
    let BB = 0;

    switch (H1) {
      case 0:
        RR = V1;
        GG = tt;
        BB = pp;
        break;
      case 1:
        RR = qq;
        GG = V1;
        BB = pp;
        break;
      case 2:
        RR = pp;
        GG = V1;
        BB = tt;
        break;
      case 3:
        RR = pp;
        GG = qq;
        BB = V1;
        break;
      case 4:
        RR = tt;
        GG = pp;
        BB = V1;
        break;
      case 5:
      default:
        RR = V1;
        GG = pp;
        BB = qq;
        break;
    }
    return { r: Math.round(Math.min(RR + .5, 255)), g: Math.round(Math.min(GG + .5, 255)), b: Math.round(Math.min(BB + .5, 255)) };
  };

  const RGBToHEX = (rgb: RGB, withNumberSign: boolean = true): string => {
    const rHex1 = rgb.r.toString(16);
    const gHex1 = rgb.g.toString(16);
    const bHex1 = rgb.b.toString(16);

    const rHex2 = `0${rHex1}`.slice(-2);
    const gHex2 = `0${gHex1}`.slice(-2);
    const bHex2 = `0${bHex1}`.slice(-2);

    return withNumberSign
      ? `#${rHex2}${gHex2}${bHex2}`
      : rHex2 + gHex2 + bHex2;
  };

  /**
  * "Red Green Blue" color space
  */
  type RGB = {
    /**
     * Red
     */
    r: number;

    /**
     * Green
     */
    g: number;

    /**
     * Blue
     */
    b: number;

    /**
     * Alpha
     */
    a?: number;
  };

  /**
  * "Hue Saturation Brightness" color space
  */
  type HSV = {
    /**
     * Hue
     */
    h: number;

    /**
     * Saturation
     */
    s: number;

    /**
     * Brightness
     */
    v: number;

    /**
     * Alpha
     */
    a?: number;
  };
}
